<template>
	<div
		class="upload"
		data-cy="upload"
	>
		<upload-page :initial-active="currentPage === 'image-manager'">
			<image-manager
				v-if="renderComponent"
				:medias="medias"
				:quantity="quantity"
				:minimum-files="minimumFiles"
				:maximum-files="maximumFiles"
				:crop-ratio="cropRatio"
				:maximum-size="maximumSize"
				:extensions="extensions"
				:selection="selection"
				:unsplash="unsplash"
				:unsplash-app-id="unsplashAppId"
				:unsplash-secret="unsplashSecret"
				:uploader="uploader"
				:orderable="orderable"
				:minimum-width="minimumWidth"
				:minimum-height="minimumHeight"
				:errors="errors"
				:api="api"
				:opened-tab="openedTab"
				:last-search-term="lastSearchTerm"
				@toggle="toggleSelection($event)"
				@changed-tab="openedTab = $event"
				@changed-search="lastSearchTerm = $event"
				@order-changed="selection = $event"
				@close="$emit('close')"
				@preview="openPreview($event, 'image')"
				@save="handleSave"
				@delete="deleteMedia"
				@hasError="handleErrors"
				@errorsClosed="errors = {}"
			/>
		</upload-page>

		<helio-toast
			:active="toastVisible && currentPage === 'image-crop'"
			:title="$t('upload.toast.errors.title')"
			:subtitle="toastSubtitle"
			:countdown="5"
			icon-weight="light"
			data-cy="toast"
			icon="exclamation-triangle"
			state="alert"
			@close="toastVisible = false"
		/>

		<upload-page :initial-active="currentPage === 'preview'">
			<preview
				v-if="renderComponent"
				:media="previewMedia"
				:type="previewType"
				@close="currentPage = `${previewType}-manager`"
			/>
		</upload-page>

		<upload-page :initial-active="currentPage === 'image-crop'">
			<image-crop
				v-if="renderComponent"
				:items="toCrop"
				:crop-ratio="cropRatio"
				:api="api"
				:minimum-width="minimumWidth"
				:minimum-height="minimumHeight"
				@cancel-crop="removeCropFromList"
				@crop="processCrop"
			/>
		</upload-page>

		<upload-page :initial-active="currentPage === 'video-manager'">
			<video-manager
				v-if="renderComponent"
				:medias="medias"
				:quantity="quantity"
				:selection="selection"
				:api="api"
				@toggle="toggleSelection($event)"
				@create="addMedia($event)"
				@order-changed="selection = $event"
				@close="$emit('close')"
				@preview="openPreview($event, 'video')"
				@save="handleSave"
				@delete="deleteMedia"
			/>
		</upload-page>
	</div>
</template>

<script>
import _has from 'lodash/has';
import _remove from 'lodash/remove';
import { format } from 'date-fns';
import Page from './page/Page.vue';
import ImageManager from './pages/image-manager/ImageManager.vue';
import ImageCrop from './pages/image-crop/ImageCrop.vue';
import VideoManager from './pages/video-manager/VideoManager.vue';
import Preview from './pages/preview/Preview.vue';
import Centios from '../../utils/communication/Centios';
import Toast from '../toast/Toast.vue';

export default {
	name: 'Upload',

	components: {
		'upload-page': Page,
		'image-manager': ImageManager,
		'video-manager': VideoManager,
		'image-crop': ImageCrop,
		'helio-toast': Toast,
		Preview,
	},

	props: {
		api: {
			type: String,
			required: true,
			description: 'Sets the Upload component\'s API route',
		},

		crop: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether images will be cropped',
		},

		cropRatio: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the ratio of the crop',
		},

		extensions: {
			type: Array,
			required: false,
			default: () => ['image/jpeg', 'image/png', 'image/gif'],
			description: 'Sets the list of accepted file extensions',
		},

		initialMedias: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Supplies the media that will be available for selection on load',
		},

		maximumFiles: {
			type: Number,
			required: false,
			default: null,
			validator: maximumFiles => maximumFiles > 0,
			description: 'Sets the maximum number of files permitted',
		},

		maximumSize: {
			type: Number,
			required: false,
			default: 5000000,
			validator: maximumSize => maximumSize > 0,
			description: 'Sets the maximum size of each file',
		},

		minimumFiles: {
			type: Number,
			required: false,
			default: null,
			validator: minimumFiles => minimumFiles > 0,
			description: 'Sets the minimum number of files required',
		},

		minimumHeight: {
			type: Number,
			required: false,
			default: 0,
			validator: minimumHeight => minimumHeight >= 0,
			description: 'Sets the minimum height of Upload',
		},

		minimumWidth: {
			type: Number,
			required: false,
			default: 0,
			validator: minimumWidth => minimumWidth >= 0,
			description: 'Sets the minimum width of Upload',
		},

		orderable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the selected media items can be reordered by the user',
		},

		quantity: {
			type: Number,
			required: false,
			default: null,
			validator: quantity => quantity > 0,
			description: 'Sets the exact number of media items required',
		},

		type: {
			type: String,
			required: false,
			default: 'image',
			validator: value => [
				'image',
				'video',
			].indexOf(value) !== -1,
			description: 'Sets Upload\'s media type',
		},

		unsplash: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether a user can access Unsplash media',
		},

		unsplashAppId: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Unsplash app ID route',
		},

		unsplashSecret: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Unsplash secret route',
		},

		uploader: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the drag-and-drop/browse uploader will appear',
		},
	},

	data() {
		return {
			renderComponent: true,
			currentPage: null,
			selection: [],
			medias: [],
			cachedMedias: [],
			toCrop: [],
			previewMedia: null,
			previewType: null,
			errors: {},
			toastVisible: false,
			initialized: false,
			openedTab: null,
			lastSearchTerm: null,
		};
	},

	computed: {
		toastSubtitle() {
			if (this.errors.totalInvalidFiles) {
				return this.$tc(
					'upload.toast.errors.message',
					this.errors.totalInvalidFiles,
					[this.errors.totalInvalidFiles],
				);
			}

			return '';
		},

		belowSpecificQuantity() {
			return this.quantity && this.selection.length < this.quantity;
		},

		belowMaximumQuantity() {
			return !this.quantity && this.maximumFiles && this.selection.length < this.maximumFiles;
		},

		noQuantitySpecified() {
			return !this.quantity && !this.minimumFiles && !this.maximumFiles;
		},
	},

	watch: {
		currentPage() {
			this.$parent.$emit('pageChange', this.currentPage);
		},

		selection() {
			this.$forceUpdate();
		},

		toCrop() {
			if (this.toCrop.length > 0) {
				this.currentPage = 'image-crop';
			} else {
				this.currentPage = 'image-manager';
			}
		},

		initialMedias() {
			this.initializeMedias();
		},

		renderComponent() {
			if (!this.renderComponent) {
				this.renderComponent = true;
			}
		},
	},

	beforeCreate() {
		this.$root.$off('initiate-crop');
	},

	created() {
		this.initializeMedias();

		this.$root.$on('initiate-crop', (media) => {
			if (this.crop) {
				this.toCrop.push(media);
			} else {
				this.createImage(media);
			}
		});
	},

	methods: {
		initializeMedias() {
			if (this.initialized) {
				return;
			}

			this.medias = [];

			if (this.type === 'image') {
				this.initializeImageUpload();
			} else if (this.type === 'video') {
				this.initializeVideoUpload();
			}

			this.initialized = true;
			this.renderComponent = false;
		},

		initializeImageUpload() {
			this.currentPage = 'image-manager';

			// We evaluate each media and discard the one's that
			// doesn't contains our required fields
			this.initialMedias.forEach((media) => {
				if (
					_has(media, 'name')
					&& _has(media, 'thumbnail')
					&& _has(media, 'url')
					&& _has(media, 'created_at')
					&& _has(media, 'id')
					&& (!this.cropRatio || this.cropRatio === media.ratio)
				) {
					this.addCreationDateToMedia(media);
					this.medias.push(media);

					if (
						media.selected
						&& (this.selection.length < this.quantity || !this.quantity)
						&& !this.selection.includes(media.id)
					) {
						this.addToSelection(media.id);
					}
				}
			});
		},

		initializeVideoUpload() {
			this.currentPage = 'video-manager';

			// We evaluate each media and discard the one's that
			// doesn't contains our required fields
			this.initialMedias.forEach((media) => {
				if (
					_has(media, 'name')
					&& _has(media, 'url')
					&& _has(media, 'thumbnail')
					&& _has(media, 'author')
					&& _has(media, 'website')
					&& _has(media, 'id')
				) {
					this.medias.push(media);

					if (media.selected) {
						this.selection[0] = media.id;
					}
				}
			});
		},

		addCreationDateToMedia(media) {
			media.creationDate = format(media.created_at, this.$t('upload.date-format'));
		},

		toggleSelection(id) {
			// this is the function that handles updating the "selected images" array.
			if (this.quantity) {
				if (
					!this.selection.includes(id)
					&& (this.selection.length < this.quantity || this.quantity === 1)
				) {
					if (this.quantity === 1) {
						this.resetSelection();
					}

					this.addToSelection(id);
				} else if (this.selection.includes(id)) {
					this.removeFromSelection(id);
				}
			}

			if (!this.quantity && (this.minimumFiles || this.maximumFiles)) {
				// handle the min file and max files count here.
				if (!this.selection.includes(id)) {
					if (this.maximumFiles && this.maximumFiles.length === 1) {
						this.resetSelection();
					}

					if (
						(this.maximumFiles && this.selection.length < this.maximumFiles)
						|| (!this.maximumFiles)
					) {
						this.addToSelection(id);
					}
				} else if (this.selection.includes(id)) {
					this.removeFromSelection(id);
				}
			}

			if (!this.quantity && !this.minimumFiles && !this.maximumFiles) {
				if (!this.selection.includes(id)) {
					this.addToSelection(id);
				} else if (this.selection.includes(id)) {
					this.removeFromSelection(id);
				}
			}
		},

		resetSelection() {
			this.selection.forEach((item) => {
				this.removeFromSelection(item);
			});
		},

		addToSelection(id) {
			this.selection.push(id);

			const media = this.medias.find(iteratedMedia => iteratedMedia.id === id);
			media.selected = true;
		},

		removeFromSelection(id) {
			this.selection = _remove(this.selection, iteratedId => iteratedId !== id);

			const media = this.medias.find(iteratedMedia => iteratedMedia.id === id);
			media.selected = false;
		},

		createImage(media) {
			const endpoint = `${this.api}/medias`;
			const formData = new FormData();
			formData.append('name', media.name);
			formData.append('width', media.width);
			formData.append('height', media.height);
			formData.append('upload-method', media.type);
			formData.append('ratio', this.cropRatio);
			formData.append('url', media.full ? media.full : media.url);
			formData.append('thumbnail', media.thumbnail);
			formData.append('author', media.author);
			formData.append('website', media.website);

			Centios.submit('post', endpoint, formData).then((response) => {
				if (Centios.isValid(response.data)) {
					this.addMedia(response.data.media);
				}
			});
		},

		addMedia(media) {
			// This is where you need to set the logic for the auto select uploaded images
			this.cachedMedias.push(media);

			if (this.toCrop.length === 0) {
				this.cachedMedias.forEach((cachedMedia) => {
					const currentCachedMedia = this.cloneObject(cachedMedia);

					currentCachedMedia.selectable = true;
					this.addCreationDateToMedia(currentCachedMedia);

					this.medias.unshift(currentCachedMedia);

					this.$root.$emit('add-media-in-gallery', currentCachedMedia.thumbnail);
					this.$emit('created', currentCachedMedia);

					if (this.belowSpecificQuantity || this.belowMaximumQuantity	|| this.noQuantitySpecified) {
						this.addToSelection(currentCachedMedia.id);
					}
				});

				this.$forceUpdate();
				this.cachedMedias = [];
			}
		},

		removeCropFromList() {
			if (this.toCrop.length > 0) {
				this.toCrop.shift();
			}
		},

		processCrop(media) {
			this.createImage(media);
			this.removeCropFromList();
		},

		handleSave() {
			// eslint-disable-next-line
			const medias = this.selection.map(selection => this.medias.find(media => media.id === selection));

			this.$emit('submit', medias);
		},

		handleErrors(value) {
			this.errors = value;
			this.toastVisible = 'hasErrors' in value;
		},

		deleteMedia(media) {
			this.medias = this.medias.filter(item => item.id !== media.id);
			this.selection = this.selection.filter(item => item !== media.id);
			this.$emit('deleted', media);
			this.$forceUpdate();
		},

		openPreview(media, type) {
			this.previewMedia = media;
			this.previewType = type;
			this.currentPage = 'preview';
		},
	},
};
</script>

<style lang="scss" src="./upload.scss" />
